import { useCallback, useState } from 'react';

/**
 * Reads and JSON-parses a sessionStorage value, falling back when the key is
 * absent, sessionStorage is unavailable, or the stored value fails to parse.
 */
export function readSessionStorage<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw !== null) return JSON.parse(raw) as T;
  } catch {
    // sessionStorage unavailable or value unparseable — fall through
  }
  return fallback;
}

/** JSON-serializes and writes a value to sessionStorage, ignoring write failures. */
export function writeSessionStorage<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage unavailable — ignore
  }
}

/**
 * Session-scoped persisted state — a drop-in `useState` replacement backed by
 * `sessionStorage`.
 *
 * The value resets to `defaultValue` at the start of each new browser
 * tab/window (sessionStorage is cleared when the tab closes) but survives
 * page refresh and in-app navigation within the same tab, since sessionStorage
 * persists across both.
 *
 * Use this for "current view state" (active filter, grouping, column order)
 * that should reset per session. For state that should persist permanently
 * across sessions (e.g. named saved presets), use localStorage directly.
 */
export function useSessionStorageState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() =>
    readSessionStorage(key, defaultValue)
  );

  const setPersistedState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState(prev => {
        const next =
          typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
        writeSessionStorage(key, next);
        return next;
      });
    },
    [key]
  );

  return [state, setPersistedState];
}
